import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  HelpCircle,
  LogOut,
  Building2,
  CalendarDays,
  CreditCard,
  Send,
  BarChart3,
  Shield,
  Sparkles,
  Calculator,
} from 'lucide-react';
import { crmAuth } from '@/services/auth/crm-auth';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

// CRM navigation items
const crmNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/crm/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Messages',
    href: '/crm/messages',
    icon: Send,
  },
  {
    title: 'Deadlines',
    href: '/crm/deadlines',
    icon: CalendarDays,
  },
  {
    title: 'AI Assistant',
    href: '/crm/ai-chat',
    icon: Sparkles,
  },
  {
    title: 'Financials',
    href: '/crm/financials',
    icon: BarChart3,
  },
  {
    title: 'Compliance',
    href: '/crm/compliance',
    icon: Shield,
  },
  {
    title: 'Tools',
    href: '/crm/tools',
    icon: Calculator,
  },
  {
    title: 'Support Chat',
    href: '/crm/chat',
    icon: MessageSquare,
  },
  {
    title: 'Subscription',
    href: '/crm/subscription',
    icon: CreditCard,
  },
  {
    title: 'Help & FAQs',
    href: '/crm/help',
    icon: HelpCircle,
  },
];

function NavMain({ items, label }: { items: NavItem[]; label?: string }) {
  const location = useLocation();

  return (
    <SidebarGroup className="px-2 py-0">
      {label && (
        <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              asChild
              isActive={
                item.href === '/crm/dashboard'
                  ? location.pathname === '/crm/dashboard' || location.pathname === '/crm/dashboard/'
                  : location.pathname === item.href ||
                    location.pathname.startsWith(item.href + '/')
              }
              tooltip={{ children: item.title }}
            >
              <Link to={item.href}>
                <item.icon className="h-4 w-4" />
                <span>{item.title}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavUser() {
  const navigate = useNavigate();
  const client = crmAuth.getClient();

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'CL';
  };

  const handleLogout = async () => {
    await crmAuth.signOut();
    navigate('/crm');
  };

  return (
    <SidebarMenu>
      {/* User Info */}
      <SidebarMenuItem>
        <SidebarMenuButton size="lg">
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs">
              {getInitials(client?.name, client?.email)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold">
                {client?.name || client?.email?.split('@')[0] || 'Client'}
              </span>
              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                Client
              </Badge>
            </div>
            <span className="truncate text-xs text-muted-foreground">
              {client?.email || ''}
            </span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {/* Logout Button */}
      <SidebarMenuItem>
        <SidebarMenuButton
          size="default"
          onClick={handleLogout}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" />
          <span>Log out</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// Sample tenant data - in production this would come from tenant context/API
const SAMPLE_TENANT = {
  name: 'Smith & Associates',
  logo: null as string | null, // Tenant logo URL - null means use placeholder
};

export function CRMSidebar() {
  // In production, get tenant info from context or API
  const tenant = SAMPLE_TENANT;

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <div className="flex flex-col items-center py-4 group-data-[collapsible=icon]:py-2">
          {/* Tenant Logo */}
          <Link to="/crm/dashboard" className="flex flex-col items-center gap-2">
            <Avatar className="h-16 w-16 rounded-lg group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8">
              {tenant.logo ? (
                <AvatarImage src={tenant.logo} alt={tenant.name} />
              ) : null}
              <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xl group-data-[collapsible=icon]:text-sm font-semibold">
                <Building2 className="h-8 w-8 group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="text-center group-data-[collapsible=icon]:hidden">
              <p className="font-semibold text-sm">{tenant.name}</p>
              <p className="text-[10px] text-muted-foreground">Client Portal</p>
            </div>
          </Link>

          {/* Powered by RoniDoc */}
          <div className="mt-3 pt-3 border-t w-full text-center group-data-[collapsible=icon]:hidden">
            <p className="text-[10px] text-muted-foreground">
              Powered by <span className="font-medium">RoniDoc</span>
            </p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={crmNavItems} label="Menu" />
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
